/**
 * „Teilen“ eines Projekts: wer darf mit, mit welchem Recht, und wer traegt
 * wie viel vom Speicher bei.
 *
 * Nur der Ersteller aendert etwas; alle anderen sehen dasselbe Blatt zum
 * Nachlesen. Gespeichert wird erst mit „Fertig“ – der Speicher der App
 * (firebase/cloud-store.ts) zieht dann die Freigabe fuer alles im Projekt
 * nach, und die Regeln des Servers pruefen sie (firestore.rules).
 */
import type { Member, Project } from '../../types.ts';
import { h } from '../../ui/dom.ts';
import { icon } from '../../ui/icons.ts';
import { toast } from '../../ui/toast.ts';
import { shareRing } from '../../ui/share-ring.ts';
import type { SharingPersonInfo } from '../../platform/sharing-api.ts';
import { sharingApi } from '../../platform/sharing-api.ts';
import { MAX_MEMBERS, contribution, evenShares, normalizeShares } from '../../firebase/sharing.ts';
import { saveProject } from '../../db/repo.ts';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1).replace('.', ',')} MB`;
}

const ROLE_TEXT: Record<Member['role'], string> = { owner: 'Ersteller', editor: 'Mitarbeiten', viewer: 'Nur lesen' };

/** Anteile neu verteilen, wenn jemand dazukommt: faire Scheibe, die anderen schrumpfen anteilig. */
function withNewcomer(shares: Record<string, number>, ids: string[], newcomer: string): Record<string, number> {
  const fair = 100 / (ids.length + 1);
  const scaled = Object.fromEntries(ids.map((id) => [id, (shares[id] ?? 0) * (1 - fair / 100)]));
  return { ...scaled, [newcomer]: fair };
}

/** Ein Wert fest, der Rest anteilig auf die anderen. */
function setOne(shares: Record<string, number>, ids: string[], id: string, value: number): Record<string, number> {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  const others = ids.filter((x) => x !== id);
  const rest = others.reduce((n, x) => n + (shares[x] ?? 0), 0);
  const out: Record<string, number> = { [id]: v };
  for (const x of others) out[x] = rest > 0 ? ((shares[x] ?? 0) / rest) * (100 - v) : (100 - v) / others.length;
  return out;
}

export async function openShareDialog(project: Project, onSaved: () => void): Promise<void> {
  const api = sharingApi();
  if (!api) {
    toast('Teilen gibt es mit Cloud-Anmeldung, sobald die Freigaben eingerichtet sind.');
    return;
  }
  const me = api.me;
  const start: Member[] = project.members?.length ? project.members : [{ id: me, name: '', role: 'owner' }];
  const members: Member[] = start.map((m) => ({ ...m }));
  const owner = members.find((m) => m.role === 'owner')?.id ?? me;
  // Ersteller – oder Stufe 3 mit „Mitarbeiten“ (firestore.rules); der Ersteller bleibt.
  const isOwner = owner === me || (api.canManageShared?.() === true && members.some((m) => m.id === me && m.role === 'editor'));
  const people: SharingPersonInfo[] = await api.people().catch(() => []);
  const nameOf = (id: string) => people.find((p) => p.id === id)?.name || members.find((m) => m.id === id)?.name || (id === me ? 'Du' : 'Unbekannt');
  const ids = () => members.map((m) => m.id);
  let shares = normalizeShares(project.storageShares, ids(), owner);
  const bytes = api.projectBytes(project.id);
  const quota = api.quotaBytes;
  let dirty = false;

  /* -------------------------------------------------------- Personen */

  const peopleList = h('div', { class: 'share__people' });
  const addBox = h('div', { class: 'share__add' });

  function paintPeople(): void {
    peopleList.replaceChildren(
      ...members.map((m, i) => {
        const role =
          m.role === 'owner' || !isOwner
            ? h('span', { class: 'share__role', text: ROLE_TEXT[m.role] })
            : h(
                'div',
                { class: 'segmented segmented--small', attrs: { role: 'group', 'aria-label': `Recht für ${nameOf(m.id)}` } },
                ...(['editor', 'viewer'] as const).map((r) =>
                  h('button', {
                    class: 'segmented__item' + (m.role === r ? ' segmented__item--on' : ''),
                    text: ROLE_TEXT[r],
                    attrs: { type: 'button', 'aria-pressed': String(m.role === r) },
                    on: {
                      click: () => {
                        m.role = r;
                        dirty = true;
                        paintPeople();
                      },
                    },
                  }),
                ),
              );
        const remove =
          isOwner && m.role !== 'owner'
            ? h('button', {
                class: 'share__remove',
                attrs: { type: 'button', 'aria-label': `${nameOf(m.id)} entfernen`, title: 'Entfernen' },
                on: {
                  click: () => {
                    members.splice(members.indexOf(m), 1);
                    shares = normalizeShares(shares, ids(), owner);
                    dirty = true;
                    paintAll();
                  },
                },
              }, icon('x'))
            : null;
        return h(
          'div',
          { class: 'share__person' },
          h('span', { class: `share__avatar share-c${i % 6}`, text: (nameOf(m.id).trim()[0] ?? '?').toUpperCase() }),
          h('span', { class: 'share__name', text: m.id === me && nameOf(m.id) !== 'Du' ? `${nameOf(m.id)} (du)` : nameOf(m.id) }),
          role,
          remove,
        );
      }),
    );

    // Hinzufuegen: freigeschaltete Personen, die noch nicht dabei sind.
    const candidates = people.filter((p) => !ids().includes(p.id)).sort((a, b) => a.name.localeCompare(b.name, 'de'));
    addBox.replaceChildren();
    if (!isOwner) return;
    if (members.length >= MAX_MEMBERS) {
      addBox.append(h('p', { class: 'hint', text: `Höchstens ${MAX_MEMBERS} Personen je Projekt.` }));
      return;
    }
    if (candidates.length === 0) {
      addBox.append(h('p', { class: 'hint', text: 'Alle freigeschalteten Personen sind schon dabei.' }));
      return;
    }
    addBox.append(
      h('p', { class: 'share__label', text: 'Hinzufügen' }),
      h(
        'div',
        { class: 'share__chips' },
        ...candidates.map((p) =>
          h(
            'button',
            {
              class: 'share__chip',
              attrs: { type: 'button' },
              on: {
                click: () => {
                  shares = withNewcomer(shares, ids(), p.id);
                  members.push({ id: p.id, name: p.name, role: 'editor' });
                  shares = normalizeShares(shares, ids(), owner);
                  dirty = true;
                  paintAll();
                },
              },
            },
            icon('plus'),
            h('span', { text: p.name || 'Ohne Namen' }),
          ),
        ),
      ),
    );
  }

  /* --------------------------------------------------------- Speicher */

  const storageBox = h('div', { class: 'share__storage' });

  function paintStorage(): void {
    const parts = members.map((m) => ({ id: m.id, label: nameOf(m.id) }));
    const legend = h('div', { class: 'share__legend' });
    const ring = shareRing({
      parts,
      values: shares,
      editable: isOwner && members.length > 1,
      center: formatBytes(bytes),
      sub: members.length > 1 ? (isOwner ? 'Grenzen ziehen' : 'Projekt-Speicher') : 'Projekt-Speicher',
      onChange: (v) => {
        shares = v;
        dirty = true;
        paintLegend();
      },
    });

    function paintLegend(): void {
      legend.replaceChildren(
        ...members.map((m, i) => {
          const pct = shares[m.id] ?? 0;
          const value = isOwner && members.length > 1
            ? (() => {
                const input = h('input', { class: 'share__pct', attrs: { type: 'number', min: 0, max: 100, step: 1, inputmode: 'numeric', 'aria-label': `Anteil ${nameOf(m.id)} in Prozent` } });
                input.value = String(pct);
                input.addEventListener('change', () => {
                  shares = normalizeShares(setOne(shares, ids(), m.id, Number(input.value) || 0), ids(), owner);
                  dirty = true;
                  ring.set(shares);
                  paintLegend();
                });
                return h('label', { class: 'share__pct-wrap' }, input, h('span', { text: '%' }));
              })()
            : h('span', { class: 'share__pct-text', text: `${pct} %` });
          return h(
            'div',
            { class: 'share__row' },
            h('span', { class: `share__dot share-c${i % 6}` }),
            h('span', { class: 'share__row-name', text: nameOf(m.id) }),
            h('span', { class: 'share__bytes', text: formatBytes(contribution(bytes, shares, m.id)) }),
            value,
          );
        }),
      );
    }
    paintLegend();

    const even =
      isOwner && members.length > 1
        ? h(
            'button',
            {
              class: 'btn btn--small share__even',
              attrs: { type: 'button' },
              on: {
                click: () => {
                  shares = evenShares(ids(), owner);
                  dirty = true;
                  ring.set(shares);
                  paintLegend();
                },
              },
            },
            h('span', { text: 'Gleich verteilen' }),
          )
        : null;
    storageBox.replaceChildren(h('div', { class: 'share__ring-row' }, ring.el, legend));
    if (even) storageBox.append(even);
    storageBox.append(
      h('p', {
        class: 'hint',
        text: `Aus wessen Speicher das Projekt kommt. Jede Person hat ${formatBytes(quota)} bei Schnittstelle Rot; ihr Anteil zählt dort mit.`,
      }),
    );
  }

  function paintAll(): void {
    paintPeople();
    paintStorage();
  }

  /* ------------------------------------------------------------ Blatt */

  const close = () => {
    document.removeEventListener('keydown', onKey);
    modal.remove();
  };
  const onKey = (ev: KeyboardEvent) => ev.key === 'Escape' && close();

  async function save(): Promise<void> {
    if (!dirty) return close();
    const next: Project = { ...project, members, storageShares: normalizeShares(shares, ids(), owner) };
    close();
    await saveProject(next);
    toast(members.length > 1 ? `Geteilt mit ${members.length - 1} ${members.length === 2 ? 'Person' : 'Personen'}` : 'Nur noch für dich');
    onSaved();
  }

  const modal = h(
    'div',
    { class: 'modal', attrs: { role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Teilen' }, on: { click: (ev) => ev.target === modal && close() } },
    h(
      'div',
      { class: 'modal__panel share' },
      h(
        'div',
        { class: 'modal__head' },
        h('div', { class: 'share__title' }, h('h2', { class: 'modal__title', text: 'Teilen' }), h('p', { class: 'share__sub', text: project.name || 'Ohne Namen' })),
        isOwner
          ? h('div', { class: 'btn-row' }, h('button', { class: 'btn btn--small', text: 'Abbrechen', attrs: { type: 'button' }, on: { click: close } }), h('button', { class: 'btn btn--primary btn--small', text: 'Fertig', attrs: { type: 'button' }, on: { click: () => void save() } }))
          : h('button', { class: 'btn btn--small', text: 'Schließen', attrs: { type: 'button' }, on: { click: close } }),
      ),
      h(
        'div',
        { class: 'modal__body' },
        h('p', { class: 'share__label', text: 'Personen' }),
        peopleList,
        addBox,
        h('p', { class: 'share__label', text: 'Speicher' }),
        storageBox,
        isOwner ? null : h('p', { class: 'hint', text: 'Nur der Ersteller ändert Freigaben und die Aufteilung.' }),
      ),
    ),
  );

  paintAll();
  document.addEventListener('keydown', onKey);
  document.body.append(modal);
}
